"use client";

import { forwardRef } from "react";
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Icon } from "./Icon";
import { cn } from "./cn";
import * as haptics from "@/lib/native/haptics";

export type ButtonVariant = "primary" | "secondary" | "dark" | "tertiary" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";
export type ButtonTone = "default" | "orange" | "danger" | "grey";
export type HapticLevel = "light" | "medium" | "heavy";

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  iconOnly?: boolean;
  leadingIcon?: LucideIcon;
  /** Renders text inside the leading chip instead of an icon. Takes priority over leadingIcon. */
  chipText?: string;
  /** Renders a custom image inside the leading chip (e.g. brand mark).
   *  Takes priority over chipText and leadingIcon. The image is sized
   *  to fit the chip and rendered with `object-fit: contain`. */
  chipImageSrc?: string;
  /** alt text for chipImageSrc. Defaults to empty (decorative). */
  chipImageAlt?: string;
  /** Pass `null` to suppress the auto-arrow on primary buttons. */
  trailingIcon?: LucideIcon | null;
  /** Colour treatment for tertiary buttons (and any future toned variants). */
  tone?: ButtonTone;
  /** Override the haptic level. Primary buttons default to `light`; others to none. */
  haptic?: HapticLevel;
  /** Disable haptic firing entirely. */
  noHaptic?: boolean;
  children?: ReactNode;
}

function fireHaptic(level: HapticLevel) {
  if (level === "light") void haptics.tap();
  else if (level === "medium") void haptics.press();
  else void haptics.thud();
}

const ICON_SIZE: Record<ButtonSize, { lead: number; trail: number; only: number }> = {
  lg: { lead: 16, trail: 18, only: 20 },
  md: { lead: 14, trail: 16, only: 18 },
  sm: { lead: 12, trail: 14, only: 16 },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "lg",
    fullWidth = false,
    iconOnly = false,
    leadingIcon,
    chipText,
    chipImageSrc,
    chipImageAlt = '',
    trailingIcon,
    tone = "default",
    haptic,
    noHaptic = false,
    children,
    className,
    onClick,
    type = "button",
    disabled,
    ...rest
  },
  ref,
) {
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !noHaptic) {
      const level: HapticLevel | null = haptic ?? (variant === "primary" ? "light" : null);
      if (level) fireHaptic(level);
    }
    onClick?.(e);
  };

  const sizes = ICON_SIZE[size];

  // Primary buttons get an auto-arrow trailing icon unless explicitly suppressed (null).
  const resolvedTrailing: LucideIcon | null =
    trailingIcon === null
      ? null
      : trailingIcon ?? (variant === "primary" && !iconOnly ? ArrowRight : null);

  // Primary and Secondary share the chunky octagonal silhouette but
  // differ in their accents:
  //   Primary   — leading icon sits in a black "chip"; V-notch on
  //               bottom-right when a trailing icon is present.
  //   Secondary — leading icon is inline (no chip); V-notch on
  //               top-left when a leading icon is present.
  const hasLeading = !!leadingIcon || !!chipText || !!chipImageSrc;
  const showChip = variant === "primary" && !iconOnly && hasLeading;
  const showLeadNotch = variant === "secondary" && !iconOnly && hasLeading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        "mucka-btn",
        `mucka-btn-${variant}`,
        `mucka-btn-${size}`,
        tone !== "default" && `mucka-btn-tone-${tone}`,
        fullWidth && "mucka-btn-fullwidth",
        iconOnly && "mucka-btn-icononly",
        showChip && "mucka-btn-with-chip",
        showLeadNotch && "mucka-btn-with-lead-notch",
        variant === "primary" && !iconOnly && resolvedTrailing && "mucka-btn-with-trail-notch",
        className,
      )}
      {...rest}
    >
      {iconOnly ? (
        leadingIcon ? <Icon icon={leadingIcon} size={sizes.only} /> : null
      ) : (
        <>
          {hasLeading &&
            (variant === "primary" ? (
              <span className="mucka-btn-chip">
                {chipImageSrc ? (
                  <img
                    src={chipImageSrc}
                    alt={chipImageAlt}
                    className="mucka-btn-chip-image"
                  />
                ) : chipText ? (
                  <span className="mucka-btn-chip-text">{chipText}</span>
                ) : leadingIcon ? (
                  <Icon icon={leadingIcon} size={sizes.lead} />
                ) : null}
              </span>
            ) : chipImageSrc ? (
              <img
                src={chipImageSrc}
                alt={chipImageAlt}
                className="mucka-btn-chip-image"
              />
            ) : chipText ? (
              <span className="mucka-btn-chip-text">{chipText}</span>
            ) : leadingIcon ? (
              <Icon icon={leadingIcon} size={sizes.lead} />
            ) : null)}
          <span className="mucka-btn-label">{children}</span>
          {resolvedTrailing && (
            <Icon icon={resolvedTrailing} size={sizes.trail} className="mucka-btn-trail" />
          )}
        </>
      )}
    </button>
  );
});
